import { MouseEventHandler, useCallback, useMemo } from "react";

import { Stack } from "@mui/material";

import { Button } from "@covertree/ui";

import { PaymentPlanKey } from "../../types";
import { useCheckout } from "../../CheckoutProvider";

export interface PaymentMethodTabsProps {
  onPaymentMethodChange: (newPlanKey: PaymentPlanKey) => void;
  selectedPaymentPlanKey: PaymentPlanKey;
}

export function PaymentMethodTabs({
  onPaymentMethodChange,
  selectedPaymentPlanKey,
}: PaymentMethodTabsProps) {
  const { anythingLoading } = useCheckout();

  const tabs = useMemo(
    () => [
      {
        key: "annual",
        title: "ACH or Credit/Debit Card",
        selected: selectedPaymentPlanKey !== "mortgageBill",
        testId: "payment-tab-creditdebit-card",
      },
      {
        key: "mortgageBill",
        title: "Mortgage Bill",
        selected: selectedPaymentPlanKey === "mortgageBill",
        testId: "payment-tab-mortgage-bill",
      },
    ],
    [selectedPaymentPlanKey]
  );

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const newPaymentPlanKey = e.currentTarget.value as PaymentPlanKey;

      // To avoid resetting to annual
      if (
        newPaymentPlanKey !== "mortgageBill" &&
        selectedPaymentPlanKey !== "mortgageBill"
      ) {
        return;
      }

      onPaymentMethodChange(newPaymentPlanKey);
    },
    [onPaymentMethodChange, selectedPaymentPlanKey]
  );

  return (
    <Stack direction="row" spacing={2} mt={1} mb={2}>
      {tabs.map((tab) => (
        <Button
          key={tab.key}
          disabled={anythingLoading}
          color="secondary"
          value={tab.key}
          sx={(theme) => ({
            borderWidth: 1,
            borderColor: "secondary",
            borderStyle: "solid",
            height: {
              xs: theme.spacing(5),
              sm: theme.spacing(6),
            },
            padding: {
              xs: "12px",
              sm: "0 32px",
            },
            fontSize: {
              xs: "18px",
              sm: "20px",
            },
          })}
          variant={tab.selected ? "contained" : "outlined"}
          onClick={handleClick}
          data-testid={tab.testId}>
          {tab.title}
        </Button>
      ))}
    </Stack>
  );
}
